import { useState, useEffect } from 'react';
import { Tenant, AuthState } from './types/user';
import { LoginScreen } from './components/LoginScreen';
import { RegisterScreen } from './components/RegisterScreen';
import { ForgotPasswordScreen } from './components/ForgotPasswordScreen';
import { ResetPasswordScreen } from './components/ResetPasswordScreen';
import { AcceptInvitePage } from './components/AcceptInvitePage';
import { SignupTokenScreen } from './components/SignupTokenScreen';
import { UserDashboard } from './components/UserDashboard';
import { CompanySettings } from './components/CompanySettings';
import { AdminDashboard } from './components/AdminDashboard';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { useToast } from './hooks/use-toast';
import { authService, tenantService } from './services/supabase-service';
import { supabase, hasValidSupabaseConfig } from './lib/supabase';
import { impersonationService } from './services/impersonation-service';

type Screen = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'accept-invite' | 'signup-token' | 'user-dashboard' | 'company-settings' | 'admin-dashboard';

// 🔐 Lista de emails com acesso admin ao sistema
const ADMIN_EMAILS = [
  'henriquemamorim@hotmail.com',
  'enzo17111@gmail.com'
];

function EnvSetupScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      background: 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)',
      color: '#fff',
      fontFamily: 'system-ui, sans-serif',
      textAlign: 'center'
    }}>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>⚠️ Configuração necessária</h1>
      <p style={{ marginBottom: 24, opacity: 0.9 }}>
        Crie um arquivo <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: 4 }}>.env</code> na raiz do projeto com:
      </p>
      <pre style={{
        background: 'rgba(0,0,0,0.3)',
        padding: 16,
        borderRadius: 8,
        textAlign: 'left',
        fontSize: 13,
        overflow: 'auto',
        maxWidth: '100%'
      }}>
        {`VITE_SUPABASE_URL=https://seu-projeto.supabase.co
VITE_SUPABASE_ANON_KEY=sua-anon-key`}
      </pre>
      <p style={{ marginTop: 24, fontSize: 14, opacity: 0.8 }}>
        Depois reinicie o servidor: <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: 4 }}>npm run dev</code>
      </p>
    </div>
  );
}

export default function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('login');
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    tenant: null,
    isLoading: false
  });

  const toast = useToast();
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Verificar se há uma sessão de impersonation ativa ao carregar
  useEffect(() => {
    const hasActiveImpersonation = impersonationService.isImpersonating();
    setIsImpersonating(hasActiveImpersonation);
  }, []);

  // Teste simples para verificar se o React está funcionando
  useEffect(() => {
    console.log('App mounted successfully');
    document.title = 'SPACE AI';
    
    // Detectar rotas especiais
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const type = params.get('type');
    
    // Detectar reset de senha
    if (window.location.pathname === '/reset-password' && token && type === 'recovery') {
      console.log('🔐 Detectada rota de reset de senha');
      setCurrentScreen('reset-password');
      return;
    }
    
    if (token) {
      if (window.location.pathname === '/accept-invite') {
        setCurrentScreen('accept-invite');
      } else if (window.location.pathname === '/signup') {
        setCurrentScreen('signup-token');
      }
    }
  }, []);

  // Verificar se há usuário ou admin salvo no localStorage ao carregar a página
  useEffect(() => {
    // Não carregar auth se estiver em páginas especiais
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const type = params.get('type');
    
    // Bloquear auto-login em páginas de token
    if (token && window.location.pathname === '/accept-invite') {
      return;
    }
    
    // Bloquear auto-login em reset de senha
    if (window.location.pathname === '/reset-password' && token && type === 'recovery') {
      return;
    }

    const savedAuthState = localStorage.getItem('space_auth_state');
    
    if (savedAuthState) {
      try {
        const authData = JSON.parse(savedAuthState);
        if (authData.user) {
          // Verificar se é admin primeiro
          const isAdmin = ADMIN_EMAILS.includes(authData.user.email.toLowerCase());
          
          // Admins podem funcionar sem tenant
          if (!isAdmin && !authData.tenant) {
            console.warn('🚫 Usuário sem tenant, bloqueando acesso');
            localStorage.removeItem('space_auth_state');
            setCurrentScreen('login');
            return;
          }
          
          // Reconverter datas
          if (authData.user.createdAt) authData.user.createdAt = new Date(authData.user.createdAt);
          if (authData.user.lastLogin) authData.user.lastLogin = new Date(authData.user.lastLogin);
          if (authData.tenant?.createdAt) authData.tenant.createdAt = new Date(authData.tenant.createdAt);
          
          // Verificar se o tenant está ativo (apenas para não-admins com tenant)
          if (!isAdmin && authData.tenant && authData.tenant.status !== 'active') {
            console.warn('🚫 Tenant desativado, bloqueando acesso');
            localStorage.removeItem('space_auth_state');
            toast.error('Sua conta foi desativada. Entre em contato com o suporte.');
            setCurrentScreen('login');
            return;
          }
          
          setAuthState({
            isAuthenticated: true,
            user: authData.user,
            tenant: authData.tenant || null,
            isLoading: false
          });
          
          if (isAdmin) {
            setCurrentScreen('admin-dashboard');
          } else {
            // Todos os outros usuários usam o user-dashboard
            setCurrentScreen('user-dashboard');
          }
        }
      } catch (error) {
        console.error('Erro ao carregar estado de auth salvo:', error);
        localStorage.removeItem('space_auth_state');
      }
    }
  }, []);

  // Detectar login via OAuth (Google)
  useEffect(() => {
    const checkOAuthSession = async () => {
      try {
        // ✅ IGNORAR se for link de reset de senha
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        if (type === 'recovery') {
          console.log('🔐 Link de reset de senha detectado, ignorando OAuth');
          return;
        }

        // Verificar se há uma flag de logout recente
        const recentLogout = sessionStorage.getItem('recent_logout');
        if (recentLogout) {
          console.log('⏸️ Logout recente detectado, ignorando sessão OAuth');
          return;
        }
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && !authState.isAuthenticated) {
          console.log('🔐 Sessão OAuth detectada, carregando dados do usuário...');
          
          const authUser = session.user;
          
          // Aguardar um pouco para o trigger criar os registros
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Verificar se é admin ANTES de buscar usuário
          const isAdminEmail = ADMIN_EMAILS.includes(authUser.email?.toLowerCase() || '');
          
          // Buscar usuário na tabela customizada
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('id', authUser.id)
            .single();
          
          if (userError || !userData) {
            console.log('⏳ Usuário ainda não existe, criando...');
            
            // Se for admin, NÃO criar tenant
            let newTenant = null;
            if (!isAdminEmail) {
              // Criar tenant único para este usuário baseado no ID
              const tenantDomain = `user-${authUser.id}.internal`;
              
              const { data: tenant, error: tenantError } = await supabase
                .from('tenants')
                .insert({
                  name: `${authUser.user_metadata.full_name || authUser.email} - Conta`,
                  domain: tenantDomain,
                  plan: 'free',
                  status: 'active'
                })
                .select()
                .single();
              
              if (tenantError || !tenant) {
                console.error('❌ Erro ao criar tenant:', tenantError);
                toast.error('Erro ao criar conta. Tente novamente.');
                return;
              }
              
              newTenant = tenant;
              console.log('✅ Tenant criado! ID:', newTenant.id);
            } else {
              console.log('🔑 Admin detectado, não criando tenant');
            }
            
            // Criar usuário (usar mesma lógica do trigger)
            const userName = authUser.user_metadata.full_name || 
                           authUser.user_metadata.name || 
                           authUser.user_metadata.display_name ||
                           authUser.email?.split('@')[0] || 
                           'Usuário';
            
            const { data: newUser, error: newUserError } = await supabase
              .from('users')
              .insert({
                id: authUser.id,
                name: userName,
                email: authUser.email!,
                tenant_id: newTenant?.id || null, // NULL para admins
                role: 'owner',
                status: 'active'
              })
              .select()
              .single();
            
            if (newUserError || !newUser) {
              console.error('❌ Erro ao criar usuário:', newUserError);
              toast.error('Erro ao criar conta. Tente novamente.');
              return;
            }
            
            // Usar os dados recém criados
            const formattedUser = {
              id: newUser.id,
              name: newUser.name,
              email: newUser.email,
              role: newUser.role as 'owner' | 'manager' | 'user',
              tenantId: newUser.tenant_id,
              status: (newUser.status === 'suspended' ? 'inactive' : newUser.status) as 'active' | 'inactive',
              createdAt: new Date(newUser.created_at),
              lastLogin: new Date(),
              twoFactorEnabled: false
            };
            
            // Se for admin, criar tenant fictício
            const formattedTenant = newTenant ? {
              id: newTenant.id,
              name: newTenant.name,
              domain: newTenant.domain,
              plan: newTenant.plan as 'free' | 'premium',
              status: (newTenant.status === 'inactive' ? 'cancelled' : newTenant.status) as 'active' | 'suspended' | 'cancelled',
              createdAt: new Date(newTenant.created_at),
              settings: {},
              twoFactorEnabled: false,
              billing: undefined,
              contact: undefined,
              accountType: undefined,
              isPersonal: undefined,
              features: undefined
            } : {
              // Tenant fictício para admins
              id: 'admin-tenant',
              name: 'Administração',
              domain: 'admin.internal',
              plan: 'premium' as 'free' | 'premium',
              status: 'active' as 'active' | 'suspended' | 'cancelled',
              createdAt: new Date(),
              settings: {},
              twoFactorEnabled: false,
              billing: undefined,
              contact: undefined,
              accountType: undefined,
              isPersonal: undefined,
              features: undefined
            };
            
            const newAuthState = {
              isAuthenticated: true,
              user: formattedUser,
              tenant: formattedTenant,
              isLoading: false
            };
            
            localStorage.setItem('space_auth_state', JSON.stringify(newAuthState));
            setAuthState(newAuthState);
            setCurrentScreen(isAdminEmail ? 'admin-dashboard' : 'user-dashboard');
            toast.success(`Bem-vindo, ${formattedUser.name}!`);
            
            return;
          }
          
          // Buscar tenant
          const { data: tenantData, error: tenantError } = await supabase
            .from('tenants')
            .select('*')
            .eq('id', userData.tenant_id)
            .single();
          
          if (tenantError || !tenantData) {
            console.error('❌ Erro ao buscar tenant:', tenantError);
            toast.error('Erro ao carregar dados da empresa.');
            return;
          }
          
          // Formatar dados
          const formattedUser = {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            role: userData.role as 'owner' | 'manager' | 'user',
            tenantId: userData.tenant_id,
            status: (userData.status === 'suspended' ? 'inactive' : userData.status) as 'active' | 'inactive',
            createdAt: new Date(userData.created_at),
            lastLogin: new Date(),
            twoFactorEnabled: userData.two_factor_enabled || false
          };
          
          const formattedTenant = {
            id: tenantData.id,
            name: tenantData.name,
            domain: tenantData.domain,
            plan: tenantData.plan as 'free' | 'premium',
            status: (tenantData.status === 'inactive' ? 'cancelled' : tenantData.status) as 'active' | 'suspended' | 'cancelled',
            createdAt: new Date(tenantData.created_at),
            settings: tenantData.settings || {
              enabledAIs: ['openai', 'anthropic', 'google']
            },
            twoFactorEnabled: tenantData.two_factor_enabled || false,
            billing: tenantData.billing,
            contact: tenantData.contact,
            accountType: tenantData.account_type,
            isPersonal: tenantData.is_personal,
            features: tenantData.features
          };
          
          const newAuthState = {
            isAuthenticated: true,
            user: formattedUser,
            tenant: formattedTenant,
            isLoading: false
          };
          
          localStorage.setItem('space_auth_state', JSON.stringify(newAuthState));
          setAuthState(newAuthState);
          setCurrentScreen('user-dashboard');
          toast.success(`Bem-vindo de volta, ${formattedUser.name}!`);
        }
      } catch (error) {
        console.error('❌ Erro ao verificar sessão OAuth:', error);
      }
    };
    
    checkOAuthSession();
  }, [authState.isAuthenticated]);

  // ✅ Detectar reset de senha via URL hash
  useEffect(() => {
    const checkResetPasswordHash = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      
      // Se for um reset de senha, mudar para a tela de reset
      if (type === 'recovery') {
        console.log('🔐 Link de reset de senha detectado');
        setCurrentScreen('reset-password');
      }
    };
    
    checkResetPasswordHash();
    
    // Escutar mudanças no hash (para SPAs)
    window.addEventListener('hashchange', checkResetPasswordHash);
    return () => window.removeEventListener('hashchange', checkResetPasswordHash);
  }, []);

  const handleLogin = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      // Verificar se é um admin
      if (ADMIN_EMAILS.includes(email.toLowerCase())) {
        // Login do admin - só autenticação, sem buscar tabelas
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (authError) {
          throw new Error(authError.message || 'Email ou senha incorretos');
        }

        if (!authData.user) {
          throw new Error('Usuário não encontrado');
        }

        // Criar user para o admin (sem tenant)
        const adminUser = {
          id: authData.user.id,
          name: authData.user.user_metadata?.name || 'Super Admin',
          email: authData.user.email || email,
          tenantId: null,
          role: 'owner' as const,
          status: 'active' as const,
          createdAt: new Date(),
          lastLogin: new Date(),
          twoFactorEnabled: false
        };

        const newAuthState = {
          isAuthenticated: true,
          user: adminUser as any,
          tenant: null, // Admins não têm tenant
          isLoading: false
        };

        // Salvar estado no localStorage
        localStorage.setItem('space_auth_state', JSON.stringify(newAuthState));
        
        setAuthState(newAuthState as AuthState);
        
        // Admin vai para o painel admin
        setCurrentScreen('admin-dashboard');
        toast.success(`Bem-vindo, Admin!`);
        return;
      }

      // Login normal para usuários
      const { user, tenant } = await authService.login(email, password);

      // Verificar se o tenant está ativo
      if (tenant.status !== 'active') {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        toast.error('Sua conta foi desativada. Entre em contato com o suporte.');
        return;
      }

      const newAuthState = {
        isAuthenticated: true,
        user,
        tenant,
        isLoading: false
      };

      // Salvar estado no localStorage
      localStorage.setItem('space_auth_state', JSON.stringify(newAuthState));
      
      setAuthState(newAuthState);
      
      // Todos os usuários vão para o user-dashboard
      setCurrentScreen('user-dashboard');
      
      // Usar toast em vez de alert
      if (user.role === 'manager') {
        toast.success(`Bem-vindo, ${user.name}! Você tem acesso às configurações da empresa.`);
      } else {
        toast.success(`Bem-vindo, ${user.name}!`);
      }
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      if (error instanceof Error) {
        toast.error(`Erro: ${error.message}`);
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
    }
  };

  const handleGoogleLogin = async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      console.log('🔐 Iniciando login com Google...');
      await authService.loginWithGoogle();
      // O Supabase vai redirecionar para o Google e depois retornar
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      console.error('Erro no login com Google:', error);
      toast.error('Erro ao fazer login com Google. Tente novamente.');
    }
  };


  const handleRegister = async (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
  }) => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      console.log('📝 Iniciando registro...');
      
      // Criar conta básica no Supabase
      const { user, tenant } = await authService.register(data);

      console.log('✅ Conta criada! Fazendo login automático...');

      const newAuthState = {
        isAuthenticated: true,
        user,
        tenant,
        isLoading: false
      };

      // Salvar estado no localStorage
      localStorage.setItem('space_auth_state', JSON.stringify(newAuthState));
      
      setAuthState(newAuthState);
      setCurrentScreen('user-dashboard');
      
      toast.success(`Bem-vindo, ${user.name}! Complete seu perfil para começar.`);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      if (error instanceof Error) {
        toast.error(`Erro: ${error.message}`);
      } else {
        toast.error('Erro ao criar conta. Tente novamente.');
      }
    }
  };

  const handleImpersonate = async (tenantId: string, userId: string) => {
    try {
      console.log('🎭 Iniciando impersonation:', { tenantId, userId });

      // Obter dados do admin (tenant não é obrigatório para admins)
      if (!authState.user) {
        toast.error('Erro: Admin não autenticado');
        return;
      }
      
      // Verificar se é admin
      const isAdmin = ADMIN_EMAILS.includes(authState.user.email.toLowerCase());
      if (!isAdmin) {
        toast.error('Erro: Usuário não é admin');
        return;
      }

      // Buscar dados do usuário e tenant alvo
      const targetUser = await authService.getUserById(userId);
      const targetTenant = await tenantService.getTenantById(tenantId);

      if (!targetUser || !targetTenant) {
        toast.error('Usuário ou tenant não encontrado');
        return;
      }

      // Iniciar sessão de impersonation
      // Para admins sem tenant, criar um tenant fictício temporário
      const adminTenant = authState.tenant || ({
        id: 'admin-temp',
        name: 'Admin System',
        domain: 'admin',
        plan: 'enterprise',
        status: 'active',
        createdAt: new Date(),
        accountType: 'business',
        isPersonal: false,
        settings: {} as any,
        billing: {} as any,
        contact: {} as any,
        features: {} as any
      } as Tenant);
      
      const success = impersonationService.startImpersonation(
        authState.user,
        adminTenant,
        targetUser,
        targetTenant
      );

      if (!success) {
        toast.error('Erro ao iniciar impersonation');
        return;
      }

      // Atualizar estado da aplicação com o usuário impersonado
      setAuthState({
        isAuthenticated: true,
        user: targetUser,
        tenant: targetTenant,
        isLoading: false
      });

      setIsImpersonating(true);
      setCurrentScreen('user-dashboard');
      
      toast.success(`Impersonation iniciada: ${targetUser.name}`, { duration: 3000 });
      console.log('✅ Impersonation iniciada com sucesso');
    } catch (error) {
      console.error('Erro ao iniciar impersonation:', error);
      toast.error('Erro ao iniciar impersonation');
    }
  };

  const handleEndImpersonation = () => {
    try {
      console.log('🔚 Encerrando impersonation...');

      const adminState = impersonationService.endImpersonation();

      if (!adminState) {
        toast.error('Erro ao encerrar impersonation');
        return;
      }

      // Restaurar estado do admin
      setAuthState({
        isAuthenticated: true,
        user: adminState.user,
        tenant: adminState.tenant,
        isLoading: false
      });

      setIsImpersonating(false);
      setCurrentScreen('admin-dashboard');
      
      toast.success('Impersonation encerrada', { duration: 2000 });
      console.log('✅ Retornado ao painel admin');
    } catch (error) {
      console.error('Erro ao encerrar impersonation:', error);
      toast.error('Erro ao encerrar impersonation');
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🔒 Iniciando logout completo...');
      
      // Marcar logout recente para evitar re-login automático
      sessionStorage.setItem('recent_logout', 'true');
      
      // Fazer logout do Supabase Auth (remove sessão OAuth)
      await supabase.auth.signOut();
      console.log('✅ Logout do Supabase Auth realizado');
    } catch (error) {
      console.error('Erro ao fazer logout do Supabase:', error);
    }
    
    // Limpar localStorage
    localStorage.removeItem('space_auth_state');
    
    // Resetar estado
    setAuthState({
      isAuthenticated: false,
      user: null,
      tenant: null,
      isLoading: false
    });
    
    // Voltar para tela de login
    setCurrentScreen('login');
    toast.success('Logout realizado com sucesso!');
    
    // Remover flag após 2 segundos (tempo suficiente para evitar re-login automático)
    setTimeout(() => {
      sessionStorage.removeItem('recent_logout');
      console.log('✅ Flag de logout removida');
    }, 2000);
  };


  const handleCompanySettings = () => {
    setCurrentScreen('company-settings');
  };

  const handleForgotPassword = () => {
    setCurrentScreen('forgot-password');
  };

  const handleResetPasswordSuccess = () => {
    setCurrentScreen('login');
    toast.success('Senha redefinida com sucesso! Faça login com sua nova senha.');
  };

  const handleBackToLogin = () => {
    setCurrentScreen('login');
  };

  const handleTenantUpdate = async (updatedTenant: Tenant) => {
    try {
      // Atualizar no Supabase
      await tenantService.updateTenantSettings(updatedTenant.id, updatedTenant.settings);
      
      // Atualizar o tenant no estado de autenticação
      setAuthState(prev => ({
        ...prev,
        tenant: updatedTenant
      }));
      
      // Atualizar no localStorage
      const updatedAuthState = {
        ...authState,
        tenant: updatedTenant
      };
      localStorage.setItem('space_auth_state', JSON.stringify(updatedAuthState));
      
      // Mostrar notificação sobre as mudanças
      const previousCount = authState.tenant?.settings?.enabledAIs?.length || 0;
      const newCount = updatedTenant.settings?.enabledAIs?.length || 0;
      
      if (newCount > previousCount) {
        toast.success(`${newCount - previousCount} IA(s) habilitada(s) com sucesso!`);
      } else if (newCount < previousCount) {
        toast.success(`${previousCount - newCount} IA(s) desabilitada(s) com sucesso!`);
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Erro ao salvar configurações: ${error.message}`);
      } else {
        toast.error('Erro ao salvar configurações. Tente novamente.');
      }
    }
  };

  const handleBackToChat = () => {
    setCurrentScreen('user-dashboard');
  };

  const switchToLogin = () => {
    setCurrentScreen('login');
  };

  const switchToRegister = () => {
    setCurrentScreen('register');
  };

  if (!hasValidSupabaseConfig) {
    return <EnvSetupScreen />;
  }

  // Renderizar configurações da empresa (apenas para gestores)
  if (currentScreen === 'company-settings' && authState.isAuthenticated && authState.user && authState.tenant && (authState.user.role === 'owner' || authState.user.role === 'manager')) {
    return (
      <CompanySettings 
        user={authState.user} 
        tenant={authState.tenant}
        onLogout={handleLogout}
        onBackToChat={handleBackToChat}
        onTenantUpdate={handleTenantUpdate}
      />
    );
  }

  // Renderizar dashboard do usuário (tanto para usuários quanto gestores)
  if (currentScreen === 'user-dashboard' && authState.isAuthenticated && authState.user && authState.tenant) {
    return (
      <>
        {isImpersonating && (
          <ImpersonationBanner 
            impersonatedUser={authState.user}
            impersonatedTenant={authState.tenant}
            onEndImpersonation={handleEndImpersonation}
          />
        )}
        <UserDashboard 
          user={authState.user} 
          tenant={authState.tenant}
          onLogout={handleLogout}
          onCompanySettings={(authState.user.role === 'owner' || authState.user.role === 'manager') ? handleCompanySettings : undefined}
          onUserUpdate={(updatedUser) => {
            setAuthState(prev => ({ ...prev, user: updatedUser }));
            localStorage.setItem('space_auth_state', JSON.stringify({ ...authState, user: updatedUser }));
          }}
        />
      </>
    );
  }

  // Renderizar dashboard admin (para henriquemamorim@hotmail.com)
  if (currentScreen === 'admin-dashboard' && authState.isAuthenticated && authState.user) {
    return (
      <AdminDashboard 
        admin={{
          id: authState.user.id,
          name: authState.user.name,
          email: authState.user.email,
          role: 'super_admin',
          createdAt: authState.user.createdAt
        }} 
        onLogout={handleLogout}
        onImpersonate={handleImpersonate}
      />
    );
  }

  if (currentScreen === 'register') {
    return (
      <RegisterScreen
        onRegister={handleRegister}
        onSwitchToLogin={switchToLogin}
        isLoading={authState.isLoading}
      />
    );
  }

  if (currentScreen === 'forgot-password') {
    return (
      <ForgotPasswordScreen
        onBack={handleBackToLogin}
      />
    );
  }

  if (currentScreen === 'reset-password') {
    return (
      <ResetPasswordScreen
        onSuccess={handleResetPasswordSuccess}
        onBack={handleBackToLogin}
      />
    );
  }

  // Tela de aceite de convite (pública)
  if (currentScreen === 'accept-invite') {
    return <AcceptInvitePage />;
  }

  // Tela de signup via token/invite (pública)
  if (currentScreen === 'signup-token') {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const inviteToken = params.get('invite');
    
    if (!token && !inviteToken) {
      setCurrentScreen('login');
      return null;
    }

    return (
      <SignupTokenScreen 
        token={token || inviteToken || ''}
        isInviteLink={!!inviteToken}
        onSuccess={(user) => {
          // Buscar tenant do usuário
          tenantService.getTenantById(user.tenantId).then(tenant => {
            const newAuthState = {
              isAuthenticated: true,
              user: {
                id: user.id,
                name: user.name,
                email: user.email,
                tenantId: user.tenantId,
                role: user.role,
                status: user.status,
                createdAt: new Date(),
                phone: undefined
              },
              tenant,
              isLoading: false
            };

            localStorage.setItem('space_auth_state', JSON.stringify(newAuthState));
            setAuthState(newAuthState);
            setCurrentScreen('user-dashboard');
            toast.success(`Bem-vindo, ${user.name}!`);
          }).catch(() => {
            toast.error('Erro ao carregar dados da empresa');
          });
        }}
      />
    );
  }

  // Tela de login normal
  return (
    <LoginScreen
      onLogin={handleLogin}
      onGoogleLogin={handleGoogleLogin}
      onSwitchToRegister={switchToRegister}
      onForgotPassword={handleForgotPassword}
      isLoading={authState.isLoading}
    />
  );
}